import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { AnalyticItem } from '@mgm/common/components';
import ScrollBar from '@mgm/common/components/ScrollBar';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { getOrdersTotals, getSelectedLocationId } from '../../../../store/orders/selectors';

const OrdersTotalsHeader = () => {
  const { t } = useTranslation(['commissions', 'timesheets', 'dashboard']);
  const theme = useTheme();
  const getCurrency = useAppSelector(getCurrencyByCode);
  const selectedLocationId = useAppSelector(getSelectedLocationId);
  const ordersTotals = useAppSelector(getOrdersTotals);

  const dailyTotals = useMemo(() => {
    if (selectedLocationId === 0) {
      return Object.values(ordersTotals).reduce(
        (acc, totals) => {
          acc.salesTotal += totals.salesTotal;
          acc.totalOrders += totals.totalOrders;
          acc.productsTotal += totals.productsTotal;
          acc.commissionsTotal += totals.commissionsTotal;
          return acc;
        },
        {
          salesTotal: 0, totalOrders: 0, productsTotal: 0, commissionsTotal: 0,
        },
      );
    }

    return ordersTotals[selectedLocationId] || {
      salesTotal: 0, totalOrders: 0, productsTotal: 0, commissionsTotal: 0,
    };
  }, [selectedLocationId, ordersTotals]);

  return (
    <Card sx={{
      height: '88px',
    }}
    >
      <ScrollBar>
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
          sx={{ p: 2, overflow: 'auto !important' }}
        >
          <AnalyticItem
            id="total-orders"
            title={t('orders', { ns: 'timesheets' })}
            percent={100}
            price={dailyTotals.totalOrders}
            icon="solar:bag-3-linear"
            color={theme.palette.success.main}
          />
          <AnalyticItem
            id="total-products"
            title={t('products', { ns: 'dashboard' })}
            percent={100}
            price={dailyTotals.productsTotal} // Display the total number of products
            icon="fluent-mdl2:product-variant"
            color={theme.palette.error.main}
          />
          <AnalyticItem
            id="total-net-sales"
            title={t('netSales')}
            percent={100}
            price={`${(dailyTotals.salesTotal || 0)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${getCurrency().mark}`}
            icon="pepicons-print:coins"
            color={theme.palette.info.main}
          />
          <AnalyticItem
            id="total-commissions"
            title={t('commissions')}
            percent={100}
            price={`${(dailyTotals.commissionsTotal || 0)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${getCurrency().mark}`}
            icon="ph:handshake-thin"
            color={theme.palette.primary.main}
          />
        </Stack>
      </ScrollBar>
    </Card>
  );
};

export { OrdersTotalsHeader };
